import type {
  DesktopPraxaConnectedWorkBridge,
  PraxaDesktopConnectedWorkDetailResult,
  PraxaDesktopConnectedWorkState,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_CONNECTED_WORK_LOAD_ERROR =
  "Connected work couldn’t load. Saved work remains available when authorized.";
export const PRAXA_CONNECTED_WORK_DETAIL_ERROR =
  "This source detail couldn’t load. You can still open it in the provider.";
export const PRAXA_CONNECTED_WORK_PIN_ERROR =
  "Praxa couldn’t update this pin. Your connected source was not changed.";

function getBridge(): DesktopPraxaConnectedWorkBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaConnectedWork;
}

export function isConnectedWorkStateVisibleForWorkspace(
  state: PraxaDesktopConnectedWorkState | null,
  workspaceId: string,
): boolean {
  return (
    state !== null &&
    (state.workspaceId === workspaceId ||
      (state.workspaceId === null &&
        (state.status === "revoked" || state.status === "unavailable")))
  );
}

export function connectedWorkDetailAuthorityKey(
  state: PraxaDesktopConnectedWorkState | null,
  workspaceId: string,
  itemId: string | null,
): string | null {
  if (state?.workspaceId !== workspaceId || itemId === null) return null;
  const item = state.items.find((candidate) => candidate.itemId === itemId);
  return item
    ? `${item.workspaceId}:${item.connectionId}:${item.itemId}:${item.sourceRevision}`
    : null;
}

export interface DesktopPraxaConnectedWorkController {
  readonly actionError: string | null;
  readonly bridge: DesktopPraxaConnectedWorkBridge | undefined;
  readonly detail: PraxaDesktopConnectedWorkDetailResult | null;
  readonly detailError: string | null;
  readonly detailLoading: boolean;
  readonly error: string | null;
  readonly loading: boolean;
  readonly pinningItemId: string | null;
  readonly refresh: () => Promise<void>;
  readonly retryDetail: () => Promise<void>;
  readonly state: PraxaDesktopConnectedWorkState | null;
  readonly togglePinned: (itemId: string) => Promise<void>;
  readonly workspaceMismatch: boolean;
}

export function useDesktopPraxaConnectedWork(
  workspaceId: string,
  selectedItemId: string | null,
): DesktopPraxaConnectedWorkController {
  const bridge = getBridge();
  const [rawState, setRawState] = useState<PraxaDesktopConnectedWorkState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<PraxaDesktopConnectedWorkDetailResult | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pinningItemId, setPinningItemId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const eventVersionRef = useRef(0);
  const detailRequestVersionRef = useRef(0);
  const selectedAuthorityKey = connectedWorkDetailAuthorityKey(
    rawState,
    workspaceId,
    selectedItemId,
  );

  const applyState = useCallback((nextState: PraxaDesktopConnectedWorkState) => {
    setRawState(nextState);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextState = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(nextState);
    } catch {
      if (eventVersionRef.current === eventVersion) setError(PRAXA_CONNECTED_WORK_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  const loadDetail = useCallback(async () => {
    if (!bridge || selectedAuthorityKey === null || selectedItemId === null) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    const requestVersion = detailRequestVersionRef.current + 1;
    detailRequestVersionRef.current = requestVersion;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const result = await bridge.loadDetail(selectedItemId);
      if (detailRequestVersionRef.current === requestVersion) setDetail(result);
    } catch {
      if (detailRequestVersionRef.current === requestVersion) {
        setDetailError(PRAXA_CONNECTED_WORK_DETAIL_ERROR);
      }
    } finally {
      if (detailRequestVersionRef.current === requestVersion) setDetailLoading(false);
    }
  }, [bridge, selectedAuthorityKey, selectedItemId]);

  const togglePinned = useCallback(
    async (itemId: string) => {
      if (!bridge) return;
      const eventVersion = eventVersionRef.current;
      setPinningItemId(itemId);
      setActionError(null);
      try {
        const nextState = await bridge.togglePinned(itemId);
        if (eventVersionRef.current === eventVersion) applyState(nextState);
      } catch {
        if (eventVersionRef.current === eventVersion) {
          setActionError(PRAXA_CONNECTED_WORK_PIN_ERROR);
        }
      } finally {
        setPinningItemId(null);
      }
    },
    [applyState, bridge],
  );

  useEffect(() => {
    setActionError(null);
    setDetail(null);
    setDetailError(null);
    detailRequestVersionRef.current += 1;
  }, [workspaceId]);

  useEffect(() => {
    if (!bridge) {
      setRawState(null);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        eventVersionRef.current += 1;
        if (active) applyState(nextState);
      });
    } catch {
      setError(PRAXA_CONNECTED_WORK_LOAD_ERROR);
    }
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((nextState) => {
        if (active && eventVersionRef.current === eventVersion) applyState(nextState);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_CONNECTED_WORK_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  useEffect(() => {
    void loadDetail();
    return () => {
      detailRequestVersionRef.current += 1;
    };
  }, [loadDetail]);

  const state = isConnectedWorkStateVisibleForWorkspace(rawState, workspaceId) ? rawState : null;

  return {
    actionError,
    bridge,
    detail,
    detailError,
    detailLoading,
    error,
    loading,
    pinningItemId,
    refresh,
    retryDetail: loadDetail,
    state,
    togglePinned,
    workspaceMismatch:
      rawState !== null && rawState.workspaceId !== null && rawState.workspaceId !== workspaceId,
  };
}
